import React, { useState } from 'react';
import type { FC } from 'react';

import { DateTime, Duration } from 'luxon';

import { NavLinkButton } from '@shared/components/bootstrap/button';
import { Panel } from '@shared/components/bootstrap/panel';
import { Table } from '@shared/components/bootstrap/table';

import { Download as AudioDownload } from '@admin/components/audio/download';
import { Listen as AudioListen } from '@admin/components/audio/listen';
import { Pagination } from '@admin/components/pagination';
import { Formatter as PhoneFormatter } from '@admin/components/phone/formatter';
import { accountURL, editLeadURL, voicemailURL } from '@admin/config/routes';
import { useTitle } from '@admin/hooks/use_title';
import { client } from '@admin/libraries/apollo';
import { useVoicemailsListQuery } from '@admin/schema';

const DEFAULT_PAGE = 1;

export const VoicemailsList: FC = () => {
  useTitle('Voicemails');
  const [page, setPage] = useState<number>(DEFAULT_PAGE);
  const { data } = useVoicemailsListQuery({
    client,
    fetchPolicy: 'cache-and-network',
    variables: { page },
  });
  const paginated = data?.paginated;
  const results = paginated?.results;

  return (
    <Panel>
      <Panel.Body>
        <Table responsive striped>
          <thead>
            <tr>
              <th>ID</th>
              <th>Phone</th>
              <th>Duration</th>
              <th>Queue</th>
              <th>Last Call At</th>
              <th>Last Call Status</th>
              <th>Lead/Account</th>
              <th>Listen</th>
              <th>Download</th>
              <th>Created At</th>
              <th>Updated At</th>
            </tr>
          </thead>

          <tbody>
            {results?.map((voicemail) => (
              <tr key={voicemail.id}>
                <td>
                  <NavLinkButton kind="default" to={voicemailURL({ id: voicemail.id })}>
                    {voicemail.id}
                  </NavLinkButton>
                </td>
                <td>{voicemail.phoneCall.phone ? <PhoneFormatter number={voicemail.phoneCall.phone} /> : '-'}</td>
                <td>
                  {voicemail.recording ? Duration.fromISO(voicemail.recording.duration).toFormat('hh:mm:ss') : '-'}
                </td>
                <td>
                  {voicemail.phoneCall.callGroup ? (
                    <>
                      {voicemail.phoneCall.callGroup.id} - {voicemail.phoneCall.callGroup.name}
                    </>
                  ) : (
                    '-'
                  )}
                </td>
                <td>
                  {voicemail.recentPhoneCall?.startAt
                    ? DateTime.fromISO(voicemail.recentPhoneCall.startAt).toLocaleString(DateTime.DATETIME_MED)
                    : '-'}
                </td>
                <td>
                  <span className="label label-default">{voicemail.recentPhoneCall?.state ?? '-'}</span>
                </td>
                <td>
                  {(() => {
                    if (!voicemail.phoneCall.lead) return '-';
                    if (voicemail.phoneCall.lead.accountID)
                      return (
                        <NavLinkButton kind="link" to={accountURL({ id: voicemail.phoneCall.lead.accountID })}>
                          Account
                        </NavLinkButton>
                      );
                    else
                      return (
                        <NavLinkButton kind="link" to={editLeadURL({ id: voicemail.phoneCall.lead.id })}>
                          Lead
                        </NavLinkButton>
                      );
                  })()}
                </td>
                <td>
                  {voicemail.recording?.playbackURL ? (
                    <AudioListen
                      src={voicemail.recording?.playbackURL}
                      date={voicemail.createdAt ?? voicemail.updatedAt}
                    />
                  ) : (
                    '-'
                  )}
                </td>
                <td>
                  {voicemail.recording?.playbackURL ? <AudioDownload src={voicemail.recording?.playbackURL} /> : '-'}
                </td>
                <td>{DateTime.fromISO(voicemail.createdAt).toLocaleString(DateTime.DATETIME_MED)}</td>
                <td>{DateTime.fromISO(voicemail.updatedAt).toLocaleString(DateTime.DATETIME_MED)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
        {paginated && <Pagination pagination={paginated.pagination} onPage={setPage} />}
      </Panel.Body>
    </Panel>
  );
};
